import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  Trophy, Target, Users, Timer, ArrowUp, ArrowDown, 
  Play, RotateCcw, Crown, Medal, Zap, ChevronLeft, ChevronRight,
  Flame, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const COUNTDOWN_TIME = 10;

const GameModeSelector = ({ onSelect, user }) => (
  <div className="max-w-2xl mx-auto">
    <div className="text-center mb-12">
      <div className="inline-flex items-center gap-3 bg-[#F8D742] text-[#0F172A] px-6 py-2 rounded-full mb-6">
        <Zap className="w-5 h-5" />
        <span className="font-bold uppercase tracking-wider">New Game</span>
      </div>
      <h1 className="text-5xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
        MATCH STREAK
      </h1>
      <p className="text-white/80 text-lg">
        How many can you get right in a row?
      </p>
      
      {/* Login prompt */}
      {!user && (
        <div className="mt-4 bg-white/10 rounded-lg px-4 py-3 inline-block">
          <p className="text-white/80 text-sm">
            <a href="/login" className="text-[#F8D742] font-semibold hover:underline">Log in</a> to save your score to the league table
          </p>
        </div>
      )}
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      {/* Goals Game */}
      <button 
        onClick={() => onSelect("goals")}
        className="group relative bg-gradient-to-br from-[#7C2C3B] to-[#5a1f2b] rounded-2xl p-8 text-left overflow-hidden hover:scale-105 transition-transform"
        data-testid="select-goals-game"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#F8D742] rounded-full -translate-y-1/2 translate-x-1/2 opacity-20 group-hover:opacity-30 transition-opacity"></div>
        <div className="relative">
          <div className="w-16 h-16 rounded-xl bg-[#F8D742] flex items-center justify-center mb-6">
            <Target className="w-8 h-8 text-[#0F172A]" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            GOALS SCORED
          </h3>
          <p className="text-white/70">
            Guess which player has scored more goals for West Ham
          </p>
          <div className="mt-6 flex items-center gap-2 text-[#F8D742] font-semibold">
            <Play className="w-5 h-5" />
            <span>START GAME</span>
          </div>
        </div>
      </button>

      {/* Appearances Game */}
      <button 
        onClick={() => onSelect("appearances")}
        className="group relative bg-gradient-to-br from-[#2BAEE5] to-[#1a8fc4] rounded-2xl p-8 text-left overflow-hidden hover:scale-105 transition-transform"
        data-testid="select-appearances-game"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2 opacity-10 group-hover:opacity-20 transition-opacity"></div>
        <div className="relative">
          <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center mb-6">
            <Users className="w-8 h-8 text-[#2BAEE5]" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            GAMES PLAYED
          </h3>
          <p className="text-white/70">
            Guess which player has made more appearances for West Ham
          </p>
          <div className="mt-6 flex items-center gap-2 text-white font-semibold">
            <Play className="w-5 h-5" />
            <span>START GAME</span>
          </div>
        </div>
      </button>
    </div>
  </div>
);

const CountdownTimer = ({ seconds, maxSeconds }) => {
  const percentage = (seconds / maxSeconds) * 100;
  const isLow = seconds <= 3;
  
  return (
    <div className="flex flex-col items-center gap-1 md:gap-2">
      <div className={`relative w-14 h-14 md:w-20 md:h-20 ${isLow ? 'animate-pulse' : ''}`}>
        <svg className="w-full h-full -rotate-90">
          <circle
            cx="50%"
            cy="50%"
            r="42%"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="50%"
            cy="50%"
            r="42%"
            stroke={isLow ? "#ef4444" : "#F8D742"}
            strokeWidth="8"
            fill="none"
            strokeDasharray={`${2 * Math.PI * 42}`}
            strokeDashoffset={`${2 * Math.PI * 42 * (1 - percentage / 100)}`}
            strokeLinecap="round"
            className="transition-all duration-100"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xl md:text-3xl font-bold ${isLow ? 'text-red-500' : 'text-white'}`} style={{ fontFamily: 'Barlow Condensed' }}>
            {seconds}
          </span>
        </div>
      </div>
      <div className="hidden md:flex items-center gap-1 text-white/60 text-sm">
        <Timer className="w-4 h-4" />
        <span>SECONDS</span>
      </div>
    </div>
  );
};

const PlayerCard = ({ name, stat, revealed, isWinner, position }) => (
  <div className={`relative rounded-xl md:rounded-2xl p-4 md:p-8 transition-all duration-500 ${
    revealed 
      ? isWinner 
        ? 'bg-gradient-to-br from-green-500 to-green-600 md:scale-105' 
        : 'bg-gradient-to-br from-red-500 to-red-600 md:scale-95 opacity-75'
      : 'bg-gradient-to-br from-slate-700 to-slate-800'
  }`}>
    {revealed && isWinner && (
      <div className="absolute -top-2 -right-2 md:-top-3 md:-right-3 w-8 h-8 md:w-12 md:h-12 bg-[#F8D742] rounded-full flex items-center justify-center">
        <Crown className="w-4 h-4 md:w-6 md:h-6 text-[#0F172A]" />
      </div>
    )}
    
    <div className="text-center">
      <div className="text-xs uppercase tracking-wider text-white/60 mb-1 md:mb-2">
        {position}
      </div>
      <h3 className="text-lg md:text-3xl font-bold text-white mb-2 md:mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
        {name.toUpperCase()}
      </h3>
      
      {revealed ? (
        <div className="bg-white/20 rounded-lg md:rounded-xl px-4 py-2 md:px-6 md:py-4 inline-block">
          <div className="text-2xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            {stat}
          </div>
        </div>
      ) : (
        <div className="bg-white/10 rounded-lg md:rounded-xl px-4 py-2 md:px-6 md:py-4 inline-block">
          <div className="text-2xl md:text-4xl font-bold text-white/50" style={{ fontFamily: 'Barlow Condensed' }}>
            ???
          </div>
        </div>
      )}
    </div>
  </div>
);

const GamePlay = ({ gameType, onGameEnd, user, token }) => {
  const [player1, setPlayer1] = useState(null);
  const [player2, setPlayer2] = useState(null);
  const [stats, setStats] = useState({ player1: 0, player2: 0 });
  const [streak, setStreak] = useState(0);
  const [countdown, setCountdown] = useState(COUNTDOWN_TIME);
  const [revealed, setRevealed] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);
  
  const statLabel = gameType === "goals" ? "GOALS" : "GAMES";

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/match-streak/random-pair?game_type=${gameType}`);
      setPlayer1(response.data.player1);
      setPlayer2(response.data.player2);
      setStats(response.data._stats);
      setRevealed(false);
      setCountdown(COUNTDOWN_TIME);
    } catch (error) {
      toast.error("Failed to load players");
      onGameEnd(0);
    } finally {
      setLoading(false);
    }
  }, [gameType, onGameEnd]);

  const fetchNextPlayer = useCallback(async () => {
    setLoading(true);
    try {
      // Current player becomes player1
      const currentStat = stats.player2;
      const response = await axios.get(
        `${API}/match-streak/next-player?current_player_id=${player2.id}&current_stat=${currentStat}&game_type=${gameType}`
      );
      
      setPlayer1(player2);
      setPlayer2(response.data.player);
      setStats({ player1: currentStat, player2: response.data._stat });
      setRevealed(false);
      setCountdown(COUNTDOWN_TIME);
    } catch (error) {
      toast.error("Failed to load next player");
      endGame();
    } finally {
      setLoading(false);
    }
  }, [gameType, player2, stats]);

  useEffect(() => {
    fetchPlayers();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!loading && !revealed && !gameOver && countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loading, revealed, gameOver]);

  const endGame = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setGameOver(true);
    setRevealed(true);
    
    // Submit score if streak > 0 and user is logged in
    if (streak > 0 && user && token) {
      setSubmitting(true);
      try {
        await axios.post(
          `${API}/match-streak/submit-score`,
          { game_type: gameType, streak },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } catch (error) {
        console.error("Failed to submit score:", error);
      } finally {
        setSubmitting(false);
      }
    }
  }, [streak, user, token, gameType]);

  const handleGuess = (choice) => {
    if (revealed || loading || gameOver) return;
    
    if (timerRef.current) clearInterval(timerRef.current);
    setRevealed(true);
    
    const stat1 = stats.player1;
    const stat2 = stats.player2;
    const isCorrect = 
      (choice === "higher" && stat2 > stat1) ||
      (choice === "lower" && stat2 < stat1);
    
    if (isCorrect) {
      setStreak(prev => prev + 1);
      toast.success("Correct! 🎉");
      
      // Continue to next round after delay
      setTimeout(() => {
        fetchNextPlayer();
      }, 1500);
    } else {
      // End game
      setTimeout(() => {
        endGame();
      }, 1000);
    }
  };

  if (loading && !player1) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-12 h-12 border-4 border-[#F8D742] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-gradient-to-br from-[#7C2C3B] to-[#5a1f2b] rounded-3xl p-10 mb-8">
          <div className="w-24 h-24 rounded-full bg-[#F8D742] mx-auto mb-6 flex items-center justify-center">
            {streak >= 10 ? (
              <Crown className="w-12 h-12 text-[#0F172A]" />
            ) : streak >= 5 ? (
              <Medal className="w-12 h-12 text-[#0F172A]" />
            ) : (
              <Flame className="w-12 h-12 text-[#0F172A]" />
            )}
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            GAME OVER!
          </h2>
          
          <div className="bg-white/10 rounded-2xl p-6 mb-6 inline-block">
            <div className="text-sm uppercase tracking-wider text-white/60 mb-1">Your Streak</div>
            <div className="text-7xl font-bold text-[#F8D742]" style={{ fontFamily: 'Barlow Condensed' }}>
              {streak}
            </div>
          </div>
          
          <p className="text-white/80 mb-8">
            {streak === 0 
              ? "Better luck next time!"
              : streak < 5 
                ? "Nice try! Can you do better?"
                : streak < 10
                  ? "Great streak! You know your Hammers!"
                  : "Incredible! You're a true West Ham expert!"}
          </p>
          
          {!user && streak > 0 && (
            <div className="bg-white/10 rounded-xl p-4 mb-6">
              <p className="text-white/80 text-sm">
                <a href="/login" className="text-[#F8D742] underline">Log in</a> to save your score to the leaderboard!
              </p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => {
                setStreak(0);
                setGameOver(false);
                fetchPlayers();
              }}
              className="bg-[#F8D742] text-[#0F172A] hover:bg-yellow-300 font-bold px-8"
              data-testid="play-again-btn"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              PLAY AGAIN
            </Button>
            <Button 
              onClick={() => onGameEnd(streak)}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              CHANGE MODE
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-row items-center justify-between mb-4 md:mb-8 gap-2 md:gap-4">
        <div className="flex items-center gap-2 md:gap-4">
          <div className={`px-3 py-1 md:px-4 md:py-2 rounded-full font-bold text-sm md:text-base ${
            gameType === "goals" 
              ? "bg-[#7C2C3B] text-white" 
              : "bg-[#2BAEE5] text-white"
          }`}>
            {gameType === "goals" ? (
              <><Target className="w-3 h-3 md:w-4 md:h-4 inline mr-1 md:mr-2" />{statLabel}</>
            ) : (
              <><Users className="w-3 h-3 md:w-4 md:h-4 inline mr-1 md:mr-2" />{statLabel}</>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3 md:gap-6">
          <div className="text-center">
            <div className="text-xs uppercase tracking-wider text-white/60 hidden md:block">Streak</div>
            <div className="text-2xl md:text-4xl font-bold text-[#F8D742] flex items-center gap-1 md:gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Flame className="w-4 h-4 md:w-6 md:h-6" />
              {streak}
            </div>
          </div>
          
          <CountdownTimer seconds={countdown} maxSeconds={COUNTDOWN_TIME} />
        </div>
      </div>

      {/* Question */}
      <div className="text-center mb-4 md:mb-8">
        <p className="text-white text-base md:text-lg">
          Who has <span className="text-[#F8D742] font-bold">more {statLabel.toLowerCase()}</span> for West Ham?
        </p>
      </div>

      {/* Players */}
      <div className="grid grid-cols-2 gap-3 md:gap-6 mb-4 md:mb-8">
        <PlayerCard 
          name={player1?.name || "Loading..."} 
          stat={stats.player1}
          revealed={revealed}
          isWinner={revealed && stats.player1 > stats.player2}
          position="Player 1"
        />
        <PlayerCard 
          name={player2?.name || "Loading..."} 
          stat={stats.player2}
          revealed={revealed}
          isWinner={revealed && stats.player2 > stats.player1}
          position="Player 2"
        />
      </div>

      {/* Buttons */}
      {!revealed && (
        <div className="text-center">
          <p className="text-white text-xs md:text-sm mb-3 md:mb-4 uppercase tracking-wider">
            Does {player2?.name?.split(' ').pop()} have higher or lower {statLabel.toLowerCase()}?
          </p>
          <div className="flex flex-row gap-3 md:gap-4 justify-center">
            <Button 
              onClick={() => handleGuess("higher")}
              className="bg-green-500 hover:bg-green-600 text-white font-bold text-base md:text-xl px-6 py-4 md:px-12 md:py-6"
              disabled={loading}
              data-testid="higher-btn"
            >
              <ArrowUp className="w-5 h-5 md:w-6 md:h-6 mr-1 md:mr-2" />
              HIGHER
            </Button>
            <Button 
              onClick={() => handleGuess("lower")}
              className="bg-red-500 hover:bg-red-600 text-white font-bold text-base md:text-xl px-6 py-4 md:px-12 md:py-6"
              disabled={loading}
              data-testid="lower-btn"
            >
              <ArrowDown className="w-5 h-5 md:w-6 md:h-6 mr-1 md:mr-2" />
              LOWER
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const Leaderboard = ({ gameType, page, onPageChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [gameType, page]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/match-streak/leaderboard/${gameType}?page=${page}`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin w-8 h-8 border-4 border-[#F8D742] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!data || data.scores.length === 0) {
    return (
      <div className="text-center py-10">
        <Trophy className="w-16 h-16 text-white/30 mx-auto mb-4" />
        <p className="text-white/60">No scores yet. Be the first to play!</p>
      </div>
    );
  }

  return (
    <div>
      {/* Leaderboard Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-white/60 text-sm">
          Part {page} of {data.total_pages} • {data.total_entries} total entries
        </div>
      </div>

      {/* Scores Table */}
      <div className="bg-white/5 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 p-4 border-b border-white/10 text-xs uppercase tracking-wider text-white/50">
          <div className="col-span-2">Rank</div>
          <div className="col-span-7">Player</div>
          <div className="col-span-3 text-right">Streak</div>
        </div>
        
        {data.scores.map((score, index) => (
          <div 
            key={score.id}
            className={`grid grid-cols-12 gap-2 p-4 items-center ${
              index % 2 === 0 ? 'bg-white/5' : ''
            } ${score.rank <= 3 ? 'border-l-4 border-[#F8D742]' : ''}`}
          >
            <div className="col-span-2">
              {score.rank === 1 ? (
                <div className="w-8 h-8 rounded-full bg-[#F8D742] flex items-center justify-center">
                  <Crown className="w-4 h-4 text-[#0F172A]" />
                </div>
              ) : score.rank === 2 ? (
                <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center">
                  <Medal className="w-4 h-4 text-slate-700" />
                </div>
              ) : score.rank === 3 ? (
                <div className="w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center">
                  <Medal className="w-4 h-4 text-white" />
                </div>
              ) : (
                <span className="text-white/60 font-mono">{score.rank}</span>
              )}
            </div>
            <div className="col-span-7">
              <div className="font-semibold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                {score.user_name}
              </div>
              <div className="text-xs text-white/40">
                {new Date(score.created_at).toLocaleDateString('en-GB')}
              </div>
            </div>
            <div className="col-span-3 text-right">
              <span className="text-2xl font-bold text-[#F8D742]" style={{ fontFamily: 'Barlow Condensed' }}>
                {score.streak}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {data.total_pages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <Button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            variant="outline"
            size="sm"
            className="border-white/30 text-white hover:bg-white/10 disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Prev
          </Button>
          <span className="text-white/60">
            Part {page} of {data.total_pages}
          </span>
          <Button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= data.total_pages}
            variant="outline"
            size="sm"
            className="border-white/30 text-white hover:bg-white/10 disabled:opacity-30"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default function MatchStreakPage() {
  const { user, token } = useAuth();
  const [gameMode, setGameMode] = useState(null); // null, "goals", "appearances"
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardTab, setLeaderboardTab] = useState("goals");
  const [leaderboardPage, setLeaderboardPage] = useState(1);

  const handleGameEnd = (finalStreak) => {
    // Show leaderboard after game ends
    if (finalStreak > 0) {
      setShowLeaderboard(true);
      setLeaderboardTab(gameMode);
    }
    setGameMode(null);
  };

  return (
    <div className="min-h-screen bg-[#0F172A]" data-testid="match-streak-page">
      <SEO title="Match Streak Challenge" path="/match-streak" description="Test your West Ham United knowledge with the Match Streak challenge. Guess results correctly to build your streak and climb the leaderboard." />
      <Navbar />
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#7C2C3B] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#2BAEE5] rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        {(gameMode || showLeaderboard) && (
          <button 
            onClick={() => {
              setGameMode(null);
              if (!showLeaderboard) setShowLeaderboard(false);
            }}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back to Menu</span>
          </button>
        )}

        {/* Game Content */}
        {!gameMode && !showLeaderboard && (
          <GameModeSelector onSelect={(mode) => {
            setGameMode(mode);
            setShowLeaderboard(false);
          }} user={user} />
        )}

        {gameMode && !showLeaderboard && (
          <GamePlay 
            gameType={gameMode} 
            onGameEnd={handleGameEnd}
            user={user}
            token={token}
          />
        )}

        {/* Leaderboard Section */}
        {!gameMode && (
          <div className="mt-16">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Trophy className="w-8 h-8 text-[#F8D742]" />
                <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                  STREAK LEAGUE
                </h2>
              </div>
              {!showLeaderboard && (
                <Button
                  onClick={() => setShowLeaderboard(true)}
                  variant="outline"
                  className="border-[#F8D742] text-[#F8D742] hover:bg-[#F8D742] hover:text-[#0F172A]"
                >
                  VIEW FULL LEADERBOARD
                </Button>
              )}
            </div>

            <Tabs value={leaderboardTab} onValueChange={(v) => { setLeaderboardTab(v); setLeaderboardPage(1); }}>
              <TabsList className="bg-white/10 mb-6">
                <TabsTrigger value="goals" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
                  <Target className="w-4 h-4 mr-2" />
                  Goals Game
                </TabsTrigger>
                <TabsTrigger value="appearances" className="data-[state=active]:bg-[#2BAEE5] data-[state=active]:text-white">
                  <Users className="w-4 h-4 mr-2" />
                  Games Played
                </TabsTrigger>
              </TabsList>

              <TabsContent value="goals">
                <Leaderboard 
                  gameType="goals" 
                  page={leaderboardPage}
                  onPageChange={setLeaderboardPage}
                />
              </TabsContent>
              <TabsContent value="appearances">
                <Leaderboard 
                  gameType="appearances" 
                  page={leaderboardPage}
                  onPageChange={setLeaderboardPage}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
